import { useQuery } from '@apollo/client';
import type * as React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Facepile } from '@xing-com/facepile';
import { useActiveItem, useViewerData } from '@xing-com/hub';
import { mediaSmallOrTiny, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import { ProgressBadge } from '@xing-com/progress-badge';
import {
  spaceM,
  spaceS,
  xdlColorHover,
  xdlColorText,
  xdlColorHoverSecondary,
  cornerRadiusL,
  spaceXL,
  scale090,
  scale080,
} from '@xing-com/tokens';
import { addParams } from '@xing-com/xing-tracking';

import { GetVisitorsCollectionDocument } from './me-menu-item-vomp.gql-types';

type StyledLinkProps = {
  $color?: 'premium' | 'proJobs' | 'proBusiness';
  $isActive?: boolean;
};

const StyledContainer = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`;

const StyledLink = styled(Link)<StyledLinkProps>`
  border-radius: ${cornerRadiusL};
  box-sizing: border-box;
  color: ${xdlColorText};
  font-size: ${scale080};
  display: flex;
  padding: ${spaceM} ${spaceS};
  width: auto;
  align-items: center;

  ${({ $isActive }) =>
    $isActive
      ? css`
          font-weight: bold;
        `
      : css``}

  &:hover {
    background-color: ${xdlColorHover};
    text-decoration: none;
  }
  &:active {
    background-color: ${xdlColorHoverSecondary};
  }

  @media ${mediaTopBarVisible} {
    line-height: ${spaceXL};
    font-size: ${scale090};

    padding: ${spaceM} 0 ${spaceM} 0;
  }
`;

const StyledVompLink = styled(StyledLink)<StyledLinkProps>`
  flex: 1;
`;

const StyledProgressBadge = styled(ProgressBadge)`
  margin-left: ${spaceM};
`;

const StyledFacepile = styled(Facepile)`
  margin-right: ${spaceM};

  @media ${mediaSmallOrTiny} {
    padding-left: ${spaceM};
  }
`;

type MeMenuItemVompProps = {
  dataTestId?: string;
  href: string;
  title: string;
  activeItemValues?: string[];
  tracking?: Record<string, string>;
};

export const MeMenuItemVomp: React.FC<MeMenuItemVompProps> = ({
  href,
  title,
  tracking,
  activeItemValues,
  dataTestId,
}) => {
  const activeItem = useActiveItem();
  const isActive = activeItem ? activeItemValues?.includes(activeItem) : false;

  const viewerData = useViewerData();
  const score = viewerData?.data?.networkEfficiencyIndicator?.score;

  const showProfileFillingIndicator = score && score !== 1;

  const trackingProps = {
    ...tracking,
    ...(score && { expandNeffi: 'true' }),
  };
  const completeTrackingURL =
    href && trackingProps ? addParams(href, trackingProps) : href;

  const data = useQuery(GetVisitorsCollectionDocument, {
    errorPolicy: 'all',
    variables: {
      first: 3,
    },
  });

  const actors = data.data?.viewer?.visitorsList?.edges.map((edge) => {
    const id = edge.node.visitorId || '';
    const src = edge.node.profileVisitor?.profileImage32?.[0]?.url;
    const alt =
      edge.node.profileVisitor?.__typename === 'XingId'
        ? `${edge.node.profileVisitor.firstName} ${edge.node.profileVisitor.lastName}`.trim()
        : '';
    const blur =
      edge.node.profileVisitor?.__typename === 'FencedProfileVisitor';

    return { id, src, alt, blur };
  });

  return (
    <StyledContainer>
      <StyledLink
        $isActive={isActive}
        data-testid={dataTestId}
        to={completeTrackingURL}
      >
        <span>{title}</span>
        {showProfileFillingIndicator ? (
          <StyledProgressBadge
            progress={Math.round(score * 100)}
            data-testid="me-menu-item-profile-progress-badge"
          />
        ) : null}
      </StyledLink>
      <StyledVompLink
        $isActive={isActive}
        data-testid="me-menu-item-profile-visits"
        to="/profile-visits"
      >
        <StyledFacepile
          actors={actors ?? []}
          size="small"
          variant="condensed"
          maxActors={3}
        />
        <FormattedMessage
          id="MEMENU_PROFILE_VISITORS"
          values={{ total: data.data?.viewer?.visitorsList?.total }}
        />
      </StyledVompLink>
    </StyledContainer>
  );
};
